import * as React from "react";
import { Button, Input, Link } from "react-ui";

import { ACCEPT_EDIT_UNIT_NAME_CLICK, CANCEL_EDIT_UNIT_NAME_CLICK, EDIT_UNIT_NAME_CLICK } from "../../metricsAction";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { emptyRef } from "../../TypingUtils";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { useInstanceIndex } from "../../Internals/ControlContexts/PathContexts/InstanceIndexContext";
import { Icon } from "../../Icons/Icon";

import { Label, Root, Spacer } from "./UnitLabel.styles";
import { UnitLabelTestPageObject } from "./UnitLabelTestPageObject";
import { getUnitInstanceLabel } from "./UnitLabelHelpers";

export interface UnitLabelProps {
    bindingPath: AbsoluteModelPath;
    label?: string;
    onChangeLabel: (nextLabel: string) => void;
    isReadOnly?: boolean;
}

export function UnitLabel(props: UnitLabelProps): JSX.Element {
    const track = useMetrics();
    const input = React.useRef<Input>(emptyRef);
    const [edit, setEdit] = React.useState(false);
    const [editableValue, setEditableValue] = React.useState("");
    const index = useInstanceIndex();

    React.useEffect(() => {
        if (edit) {
            input.current?.setSelectionRange(0, editableValue.length);
        }
    }, [edit]);

    useTestPageObject(
        () =>
            UnitLabelTestPageObject(props.onChangeLabel, edit, editableValue, () =>
                getUnitInstanceLabel(index, props.label)
            ),
        [edit, props.onChangeLabel, editableValue, index, props.label]
    );

    const handleOnClickSave = () => {
        track(ACCEPT_EDIT_UNIT_NAME_CLICK, { path: props.bindingPath, unitName: editableValue });
        if (editableValue != undefined) {
            props.onChangeLabel(editableValue);
        }
        setEdit(false);
    };

    const handleOnClickEdit = () => {
        track(EDIT_UNIT_NAME_CLICK);
        setEditableValue(props.label ?? "");
        setEdit(true);
    };

    const handleOnClickEditCancel = () => {
        track(CANCEL_EDIT_UNIT_NAME_CLICK);
        setEdit(false);
    };

    function renderEditableLabel(): JSX.Element {
        return (
            <>
                <Label>{`Экз. ${index + 1}`}</Label>
                <Spacer />
                <Input
                    ref={input}
                    value={editableValue}
                    onValueChange={setEditableValue}
                    data-tid="UnitLabelInput"
                    maxLength={100}
                    width={350}
                />
                <Spacer />
                <Button data-tid="UnitLabelSavedButton" onClick={handleOnClickSave}>
                    Сохранить
                </Button>
                <Spacer />
                <Button use="link" data-tid="UnitLabelCancelLink" onClick={handleOnClickEditCancel}>
                    Отменить
                </Button>
            </>
        );
    }

    function renderTextLabel(): JSX.Element {
        return (
            <Label>
                {getUnitInstanceLabel(index, props.label)}
                {!props.isReadOnly && (
                    <Link
                        icon={<Icon name="ToolPencilLineIcon" size={16} weight="light" />}
                        data-tid="UnitLabelEditLink"
                        onClick={handleOnClickEdit}
                        style={{ left: 3, bottom: 2 }}
                    />
                )}
            </Label>
        );
    }

    return <Root>{edit ? renderEditableLabel() : renderTextLabel()}</Root>;
}
